import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sendung, SendungStatus, SpediteurTyp } from '../types';
import './SendungList.css';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const STATUS_LABEL: Record<SendungStatus, string> = {
  VORBEREITET: 'Vorbereitet',
  ABGEHOLT: 'Abgeholt',
  IN_TRANSIT: 'Unterwegs',
  ZUGESTELLT: 'Zugestellt',
  ZUSTELLUNG_FEHLGESCHLAGEN: 'Zustellung fehlgeschlagen',
  RETOURE_IN_TRANSIT: 'Retoure unterwegs',
};

const STATUS_CLASS: Record<SendungStatus, string> = {
  VORBEREITET: 'sl-status-vorbereitet',
  ABGEHOLT: 'sl-status-abgeholt',
  IN_TRANSIT: 'sl-status-transit',
  ZUGESTELLT: 'sl-status-zugestellt',
  ZUSTELLUNG_FEHLGESCHLAGEN: 'sl-status-fehlgeschlagen',
  RETOURE_IN_TRANSIT: 'sl-status-transit',
};

const SPEDITEUR_LABEL: Record<SpediteurTyp, string> = {
  DHL: 'DHL',
  DPD: 'DPD',
  SCHENKER: 'Schenker',
  GLS: 'GLS',
  EIGENE: 'Eigene Zustellung',
};

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return err.message;
  }
  return 'Unbekannter Fehler';
}

const SendungList: React.FC = () => {
  const [sendungen, setSendungen] = useState<Sendung[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');

  const [statusFormId, setStatusFormId] = useState<number | null>(null);
  const [neuerStatus, setNeuerStatus] = useState<SendungStatus>('IN_TRANSIT');
  const [ort, setOrt] = useState('');
  const [nachricht, setNachricht] = useState('');
  const [statusLaedt, setStatusLaedt] = useState(false);
  const [statusFehler, setStatusFehler] = useState<string | null>(null);
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFehler(null);
    setAkteId(null);
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const res = await axios.get<Sendung[]>(`${API_URL}/api/sendungen`, { params });
      setSendungen(res.data);
    } catch (err) {
      setFehler(`Sendungen konnten nicht geladen werden: ${extractError(err)}`);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openStatusForm = (s: Sendung) => {
    setStatusFehler(null);
    setStatusFormId(s.sendung_id);
    setNeuerStatus(s.status);
    setOrt('');
    setNachricht('');
  };

  const closeStatusForm = () => {
    setStatusFormId(null);
    setStatusFehler(null);
  };

  const submitStatus = async (s: Sendung) => {
    setStatusLaedt(true);
    setStatusFehler(null);
    try {
      await axios.post(`${API_URL}/api/sendungen/${s.sendung_id}/status`, {
        status: neuerStatus,
        ort: ort || null,
        nachricht: nachricht || null,
      });
      closeStatusForm();
      await load();
    } catch (err) {
      setStatusFehler(extractError(err));
    } finally {
      setStatusLaedt(false);
    }
  };

  return (
    <div className="sendung-list-container">
      <div className="list-header">
        <h2>Sendungen</h2>
        <div className="header-actions">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Status</option>
            {(Object.keys(STATUS_LABEL) as SendungStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : fehler ? (
        <div className="sl-error">
          <span>{fehler}</span>
          <button className="sl-retry-btn" onClick={load}>Erneut versuchen</button>
        </div>
      ) : sendungen.length === 0 ? (
        <div className="empty-state">
          <p>Keine Sendungen vorhanden</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="sendung-table">
            <thead>
              <tr>
                <th>Tracking-Nr.</th>
                <th>Spediteur</th>
                <th>Lieferschein</th>
                <th>Status</th>
                <th>Pakete</th>
                <th>Gewicht</th>
                <th>Erstellt</th>
                <th>Zugestellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sendungen.map(s => (
                <React.Fragment key={s.sendung_id}>
                  <tr>
                    <td className="sendung-nr">
                      {s.tracking_url ? (
                        <a href={s.tracking_url} target="_blank" rel="noopener noreferrer">
                          {s.tracking_nr || `#${s.sendung_id}`}
                        </a>
                      ) : (s.tracking_nr || `#${s.sendung_id}`)}
                    </td>
                    <td>{SPEDITEUR_LABEL[s.spediteur] || s.spediteur_freitext || s.spediteur}</td>
                    <td>Lieferschein #{s.lieferschein_id}</td>
                    <td>
                      <span className={`status-badge ${STATUS_CLASS[s.status] || ''}`}>
                        {STATUS_LABEL[s.status] || s.status}
                      </span>
                    </td>
                    <td>{s.anzahl_pakete}</td>
                    <td>{s.gewicht_kg !== null && s.gewicht_kg !== undefined ? `${s.gewicht_kg} kg` : '-'}</td>
                    <td>{formatDate(s.erstellt_am)}</td>
                    <td>{formatDate(s.zugestellt_am)}</td>
                    <td className="action-cell">
                      <button
                        className="btn-status-update"
                        onClick={() => (statusFormId === s.sendung_id ? closeStatusForm() : openStatusForm(s))}
                      >
                        {statusFormId === s.sendung_id ? 'Abbrechen' : 'Status ändern'}
                      </button>
                      <button
                        className="btn-status-update"
                        style={{ marginLeft: 6 }}
                        onClick={() => setAkteId(akteId === s.sendung_id ? null : s.sendung_id)}
                      >
                        {akteId === s.sendung_id ? 'Akte schließen' : 'Akte'}
                      </button>
                    </td>
                  </tr>
                  {statusFormId === s.sendung_id && (
                    <tr className="sl-status-row">
                      <td colSpan={9}>
                        <div className="sl-status-form">
                          <label>
                            Neuer Status
                            <select value={neuerStatus} onChange={e => setNeuerStatus(e.target.value as SendungStatus)}>
                              {(Object.keys(STATUS_LABEL) as SendungStatus[]).map(st => (
                                <option key={st} value={st}>{STATUS_LABEL[st]}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Ort
                            <input type="text" value={ort} onChange={e => setOrt(e.target.value)} />
                          </label>
                          <label className="sl-form-nachricht">
                            Nachricht
                            <input type="text" value={nachricht} onChange={e => setNachricht(e.target.value)} />
                          </label>
                          {statusFehler && <div className="sl-inline-fehler">{statusFehler}</div>}
                          <div className="sl-form-actions">
                            <button
                              className="btn-status-confirm"
                              onClick={() => submitStatus(s)}
                              disabled={statusLaedt}
                            >
                              {statusLaedt ? 'Speichert...' : 'Bestätigen'}
                            </button>
                            <button className="btn-status-cancel" onClick={closeStatusForm}>
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {akteId === s.sendung_id && (
                    <tr>
                      <td colSpan={9} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                        <AktivitaetenBlock entitaetTyp="sendung" entitaetId={s.sendung_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SendungList;
